import { useParams } from "react-router-dom";

import type { BlocklistRow, McpUserBlockState } from "../../api/mcp";
import { BlockControl } from "../../components/mcp/BlockControl";
import { Breadcrumbs } from "../../components/Layout";
import { NotSeenRecently, ResourceView } from "../../components/states";
import { Badge, Notice, Page, Panel } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";

const SCOPES: {
  key: "merchant_wide" | "user_everywhere" | "user_in_merchant";
  title: string;
  explain: string;
}[] = [
  {
    key: "merchant_wide",
    title: "The whole merchant is blocked",
    explain:
      'A ("<reference>", "*") row. Every user of this merchant is refused. Unblocking this one person will not help — the merchant block has to be lifted.',
  },
  {
    key: "user_everywhere",
    title: "This person is blocked everywhere",
    explain:
      'A ("*", email) row. They are refused at every merchant. Unblocking them here lifts only the row for this merchant, not this one.',
  },
  {
    key: "user_in_merchant",
    title: "This person is blocked at this merchant",
    explain:
      'A ("<reference>", email) row. Refused here only; unaffected at other merchants.',
  },
];

function Row({ row }: { row: BlocklistRow }) {
  return (
    <div className="mt-2 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
      {row.reason && (
        <div>
          Reason: <span className="text-slate-950 dark:text-white">{row.reason}</span>
        </div>
      )}
      {row.added_by && (
        <div>
          Added by: <span className="ref">{row.added_by}</span>
        </div>
      )}
      {row.added_at && <div>When: {new Date(row.added_at).toLocaleString()}</div>}
    </div>
  );
}

/**
 * Is this person refused at this merchant, and WHY.
 *
 * The three `matched` scopes are rendered separately rather than collapsed
 * into one "blocked: yes" — which scope applies is the only thing that tells
 * an operator which lever to pull.
 */
export function TenantUser({ config }: { config: AppConfig }) {
  const { ref = "", email = "" } = useParams();
  const path = `/mcp/admin/tenant/${encodeURIComponent(ref)}/user/${encodeURIComponent(email)}`;
  const { state, reload } = useResource<McpUserBlockState>(config, path, [ref, email]);

  return (
    <Page title={<span className="ref">{email}</span>} subtitle={`At merchant ${ref}`}>
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref, to: `/tenants/${encodeURIComponent(ref)}` },
          { label: "Users", to: `/tenants/${encodeURIComponent(ref)}/users` },
          { label: email },
        ]}
      />

      <ResourceView
        state={state}
        what="this user's block state"
        onRetry={reload}
        notFound={<NotSeenRecently reference={ref} />}
      />

      {state.kind === "ready" &&
        (() => {
          const u = state.data;
          const matched = u.matched ?? {};
          const active = SCOPES.filter((s) => matched[s.key]);

          return (
            <>
              <Panel className="p-4">
                <div className="flex items-center gap-3">
                  {u.blocked ? (
                    <Badge tone="red">Refused</Badge>
                  ) : (
                    <Badge tone="green">Allowed</Badge>
                  )}
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {u.blocked
                      ? "This request would be refused right now."
                      : "Nothing blocks this person at this merchant."}
                  </span>
                </div>
                <div className="mt-3 border-t border-slate-950/10 dark:border-white/10 pt-3">
                  <BlockControl
                    config={config}
                    scope={{ kind: "user", reference: ref, email }}
                    blocked={Boolean(matched.user_in_merchant)}
                    onDone={reload}
                  />
                  {/* An unblock here deletes only THIS row. If a wider rule
                      also matches, the person stays refused — say so before
                      they try it and conclude the API is broken. */}
                  {matched.user_in_merchant &&
                    (matched.user_everywhere || matched.merchant_wide) && (
                      <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
                        Unblocking here will not lift the wider rule below — they would
                        still be refused.
                      </p>
                    )}
                </div>
              </Panel>

              {u.blocked && active.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Why — {active.length === 1 ? "one rule" : `${active.length} rules`} apply
                  </h2>
                  {active.map((s) => (
                    <Notice key={s.key} tone="amber" title={s.title}>
                      <p>{s.explain}</p>
                      <Row row={matched[s.key]!} />
                    </Notice>
                  ))}
                  {active.length > 1 && (
                    <Notice tone="amber" title="More than one rule applies">
                      Lifting one of these will not unblock this person — every matching rule
                      has to go.
                    </Notice>
                  )}
                </div>
              )}
            </>
          );
        })()}
    </Page>
  );
}
